import { useEffect } from 'react'
import { Alert, Col, Descriptions, Form, Input, InputNumber, Modal, Row, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatDateTime } from '@/shared/lib/format'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import { acceptReturn, missingQuantity, receiveTransfer, type StockTransfer } from './stockApi'

interface TransferReceiveModalProps {
  transfer: StockTransfer | null
  onClose: () => void
}

interface FormValues {
  receivedQuantity: number
  damagedQuantity: number
  note?: string
}

export function TransferReceiveModal({ transfer, onClose }: TransferReceiveModalProps) {
  const { t } = useTranslation()
  const actors = useActorDirectory()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()

  useEffect(() => {
    if (!transfer) return
    form.setFieldsValue({ receivedQuantity: transfer.quantitySent, damagedQuantity: 0, note: '' })
  }, [transfer, form])

  const received = Form.useWatch('receivedQuantity', form) ?? 0
  const damaged = Form.useWatch('damagedQuantity', form) ?? 0
  const missing = transfer ? missingQuantity(transfer.quantitySent, received, damaged) : 0
  const isReturn = transfer?.direction === 'RETURN'

  const submit = useMutation({
    mutationFn: (values: FormValues) => {
      const receipt = {
        receivedQuantity: values.receivedQuantity,
        damagedQuantity: values.damagedQuantity,
        note: values.note?.trim() ? values.note.trim() : null,
      }
      return isReturn ? acceptReturn(transfer?.id ?? '', receipt) : receiveTransfer(transfer?.id ?? '', receipt)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stock.all })
      onClose()
    },
  })

  const close = () => {
    submit.reset()
    onClose()
  }

  return (
    <Modal
      open={transfer !== null}
      title={t(isReturn ? 'stockTransfer.returnAccept.title' : 'stockTransfer.receive.title')}
      okText={t(isReturn ? 'stockTransfer.returnAccept.submit' : 'stockTransfer.receive.submit')}
      cancelText={t('common.cancel')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => submit.mutate(values))}
      okButtonProps={{ disabled: missing < 0 }}
      confirmLoading={submit.isPending}
      destroyOnHidden
      width={620}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t(isReturn ? 'stockTransfer.returnAccept.note' : 'stockTransfer.receive.note')}
      />
      {submit.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(submit.error, t)} />
      ) : null}
      {transfer ? (
        <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label={t('stockTransfer.field.item')}>{`${transfer.itemName} · ${transfer.sku}`}</Descriptions.Item>
          <Descriptions.Item label={t('stockTransfer.field.route')}>{`${transfer.fromNodeName} → ${transfer.toNodeName}`}</Descriptions.Item>
          <Descriptions.Item label={t('stockTransfer.field.sent')}>{`${transfer.quantitySent} ${transfer.unit}`}</Descriptions.Item>
          <Descriptions.Item label={t('stockTransfer.field.dispatchedBy')}>
            {`${actorLabel(transfer.dispatchedBy, actors, t('common.dash'))} · ${formatDateTime(transfer.dispatchedAt)}`}
          </Descriptions.Item>
          <Descriptions.Item label={t('stockTransfer.field.docNo')}>{transfer.docNo ?? t('common.dash')}</Descriptions.Item>
          {isReturn ? (
            <>
              <Descriptions.Item label={t('stock.field.returnReason')}>{transfer.returnReason}</Descriptions.Item>
              <Descriptions.Item label={t('stock.field.returnCondition')}>{transfer.returnCondition}</Descriptions.Item>
              <Descriptions.Item label={t('stock.field.serialNumbers')}>
                {transfer.serialNumbers ?? t('common.dash')}
              </Descriptions.Item>
            </>
          ) : null}
        </Descriptions>
      ) : null}
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={submit.isPending}>
        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item
              name="receivedQuantity"
              label={t('stockTransfer.field.received')}
              rules={[{ required: true, message: t('stockTransfer.validation.received') }]}
            >
              <InputNumber min={0} max={transfer?.quantitySent} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              name="damagedQuantity"
              label={t('stockTransfer.field.damaged')}
              rules={[{ required: true, message: t('stockTransfer.validation.damaged') }]}
            >
              <InputNumber min={0} max={transfer?.quantitySent} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Typography.Paragraph type={missing < 0 ? 'danger' : missing > 0 ? 'warning' : 'secondary'}>
          {missing < 0
            ? t('stockTransfer.validation.exceeds')
            : missing === 0
              ? t('stockTransfer.receive.allArrived')
              : t('stockTransfer.receive.missing', { count: missing })}
        </Typography.Paragraph>
        <Form.Item name="note" label={t('stockTransfer.field.receiveNote')}>
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
